import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, ElementRef, OnDestroy, Pipe, PipeTransform, TemplateRef, ViewChild } from '@angular/core';
import { loadSituReach, situReachIfLoaded } from 'src/app/services/situreach-loader';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyType, ICompany, IdentityWebApiClient, ListPartnershipsRequest, PartnershipStatus } from 'identity-webapi-client';
import { BehaviorSubject, combineLatest, from, Observable, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { FileVaultCategory, FileVaultReferenceRelation, FileVaultSubjectType, ListMarketingMaterialsRequest, ListMarketingMaterialsResponse, ListRangesRequest, ListRangesResponse, MarketingFileReference as MFR, MarketingRole, NewListProductsRequest, Product2, Range, ResourceType, SituViewWebApiClient, UpdateMarketingFileApprovalRequest, UpdateMarketingMaterialFiltersRequest } from 'situview-webapi-client';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { AuthService, BreadcrumbService, CompanyService, ConfirmDialogService, ProductService, ProductTypeWithColor, StateService } from 'src/app/services';
import { FileVaultService } from 'src/app/services/file-vault.service';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { FileMap, FileMapEditorComponent, FileMapEditorDialogData, FileMapEditorDialogResult, imageTypes } from './file-map-editor/file-map-editor.component';

export enum FileType {
  IMAGE,
  VIDEO,
  PDF,
  MAP,
  THUMBNAIL,
}
interface MarketingFileReference extends MFR {
  positions?: Record<string, number>;
  localType?: FileType;
  badAspect?: boolean;
  usingDefault?: string;
  bigram?: string;
  thumbnail?: MarketingFileReference;
  map?: MarketingFileReference;
  mapData?: FileMap;
  border?: string;
  tooltip?: string;
}
interface MarketingSubject {
  allFiles: MarketingFileReference[];
  scope: Record<string, {
    attached: MarketingFileReference[];
    available: MarketingFileReference[];
    edited: boolean;
  }>;
}

interface PartnerFiles {
  approved: MarketingFileReference[];
  pending: MarketingFileReference[];
  rejected: MarketingFileReference[];
}

const truncateCap = 20;
@Pipe({ name: 'truncate', pure: true })
export class TruncatePipe implements PipeTransform {
  transform(files: Record<string, MarketingSubject>, subjects: { id: string }[], scope: string): MarketingFileReference[] {
    const res: MarketingFileReference[] = [];
    for (let i = 0; i < subjects?.length || 0; i++) {
      if (res.length >= truncateCap) break;
      if (files[subjects[i]?.id!]?.scope[scope]?.attached?.length > 0) {
        files[subjects[i]?.id!]?.scope[scope]?.attached.forEach(file => {
          if (res.length < truncateCap) res.push(file);
        });
      } else if (scope !== 'null' && files[subjects[i]?.id!]?.scope['null']?.attached?.length > 0) {
        files[subjects[i]?.id!]?.scope['null']?.attached.forEach(file => {
          file.usingDefault = scope;
          if (res.length < truncateCap) res.push(file);
        });
      }
    }
    return res;
  }
}

@Component({
  selector: 'app-marketing',
  templateUrl: './marketing.component.html',
  styleUrls: ['./marketing.component.scss']
})
export class MarketingComponent extends SituBaseComponent implements OnDestroy {

  @ViewChild('phonePreview') phonePreview!: TemplateRef<any>;

  readonly jsonType = 'application/json';
  readonly pdfType = 'application/pdf';
  readonly videoType = 'video/mp4';
  readonly imagetypes = ['image/jpeg', 'image/webp', 'image/svg+xml', 'image/png', 'image/gif', 'image/avif', 'image/apng'];
  readonly acceptedTypes = ['.jpg', '.jpeg', '.webp', '.svg', '.png', '.gif', '.avif', '.apng', '.mp4', '.pdf'];
  readonly imageAcceptedTypes = ['.jpg', '.jpeg', '.webp', '.svg', '.png', '.gif', '.avif', '.apng'];

  public builderType = CompanyType.Builder;
  public readonly FileVaultSubjectType = FileVaultSubjectType;
  public readonly MarketingRole = MarketingRole;

  // The role-tagged slots the co-brand tile pulls from. Each is uploaded straight
  // into its slot (upload + attach-with-role in one action); the gallery deck is
  // the separate generic-upload path on the left product rows.
  public readonly tileRoleSlots: { role: MarketingRole; label: string; accept: string[] }[] = [
    { role: MarketingRole.Hero, label: 'Hero image', accept: this.imageAcceptedTypes },
    { role: MarketingRole.Brochure, label: 'Brochure', accept: ['.pdf'] },
    { role: MarketingRole.Warranty, label: 'Warranty', accept: ['.pdf'] },
  ];
  public readonly productsPageSize = 20;

  public files: Record<string, MarketingSubject> = {};
  public partnerFiles: BehaviorSubject<PartnerFiles> = new BehaviorSubject<PartnerFiles>({ approved: [], pending: [], rejected: [] });
  public allPartners: ICompany[] = [];
  public partnerPendingFiles: Record<string, number> = {};
  public partners: ReplaySubject<ICompany[]> = new ReplaySubject<ICompany[]>(1);
  public partner: ICompany | null = null;
  public scope: string = 'null';
  public approvalView = false;

  public dragging = false;
  public FileType = FileType;
  public allRanges: Range[] = [];
  public filteredRanges: Range[] = [];
  public products: Product2[] = [];
  public pagesLoading: Record<number, boolean> = {};
  public totalItems = 0;
  public allProductTypes: ProductTypeWithColor[] = [];
  public typeFilterName = new FormControl();
  public filteredProductTypes: Observable<ProductTypeWithColor[]>;
  public productFilterGroup = new FormGroup({
    name: new FormControl(''),
    type: new FormControl(''),
  });

  public marketingFilePreview: MarketingFileReference[] | null = null;
  private previewStack: MarketingFileReference[][] = [];

  public activePreview = 0;
  private destroyed = new Subject();

  // SituReach showcase preview state — when set, the phone-mockup pane mounts
  // Showcase.open() instead of the legacy file-by-file slideshow.
  public showcasePreviewProductId: string | null = null;
  // The Product2 currently previewed (a product row's showcase is open) — drives
  // the tile-content completeness panel beside the phone mockup.
  public previewProductObj: Product2 | undefined;
  @ViewChild('showcaseHost') showcaseHost?: ElementRef<HTMLElement>;
  @ViewChild('productsPanel') productsPanel?: MatExpansionPanel;
  @ViewChild('rangesPanel') rangesPanel?: MatExpansionPanel;

  // --- Deep-linking (sticky URLs) ---
  // The page mirrors its state into query params (?builder=&view=&subject=&
  // subjectType=) so a selection can be shared/bookmarked and reopened to the
  // same pill + previewed subject. `pendingDeepLink` holds the params captured
  // at load; it's consumed once the company + partners have resolved.
  private pendingDeepLink: { builder?: string; view?: string; subject?: string; subjectType?: string } | null = null;
  private deepLinkApplied = false;
  // Suppresses URL writes while we're restoring state from a deep link, so the
  // intermediate steps don't each stamp a half-built URL.
  private restoring = false;
  // The subject currently mirrored in the URL (product/range/company preview).
  private previewSubjectId: string | null = null;
  private previewSubjectType?: FileVaultSubjectType;

  constructor(
    readonly authService: AuthService,
    readonly stateService: StateService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly companyService: CompanyService,
    private readonly situViewClient: SituViewWebApiClient,
    private readonly identityClient: IdentityWebApiClient,
    private readonly fileVaultService: FileVaultService,
    private readonly productService: ProductService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {
    super(authService, stateService);

    // Capture any deep-link params so we can restore the pill + preview once the
    // company and partner list have loaded. Snapshot (read once) — our own URL
    // writes later must not re-trigger a restore.
    const qp = this.route.snapshot.queryParamMap;
    if (qp.get('builder') || qp.get('subject') || qp.get('view')) {
      this.pendingDeepLink = {
        builder: qp.get('builder') || undefined,
        view: qp.get('view') || undefined,
        subject: qp.get('subject') || undefined,
        subjectType: qp.get('subjectType') || undefined,
      };
    }

    this.productFilterGroup.valueChanges.pipe(
      debounceTime(500),
      takeUntil(this.destroyed)
    ).subscribe((filters) => {
      this.typeFilterName.setValue(filters?.type?.name, { emitEvent: false });
      this.pagesLoading = [];
      this.getProductPage(0);
    });

    this.filteredProductTypes = combineLatest([
      from(this.loadProductTypes()),
      this.typeFilterName.valueChanges.pipe(startWith(''))]).pipe(
        takeUntil(this.destroyed),
        map(([_, input]) => this.allProductTypes.filter(pt => pt.name?.toLowerCase()?.includes(input?.toLowerCase()))
        ));
  }

  /**
   * Called from the template as rows scroll into view, to page more products
   * in. Always renders as '' so the binding is stable: printing
   * getProductPage's result directly flipped between a Promise and undefined,
   * which dev-mode change detection reports as NG0100.
   */
  requestProductPage(index: number): string {
    void this.getProductPage(index);
    return '';
  }

  ngOnDestroy(): void {
    this.closeShowcasePreview();
    this.destroyed.next();
    this.destroyed.complete();
    super.ngOnDestroy();
  }

  private mountShowcasePreview(productId: string): void {
    // The host *ngIf needs a tick to materialise the div, then we mount.
    setTimeout(() => {
      const host = this.showcaseHost?.nativeElement;
      if (!host) return;
      const builderCode = this.partner?.code || this.selectedCompany?.code || '';
      // Loaded on demand; see situreach-loader for why it is not imported up front.
      loadSituReach()
        .then((SituReach) => SituReach.Showcase.open(host, productId, builderCode, { position: 'left' }))
        .catch((err: any) => console.warn('[marketing] showcase preview failed', err));
    }, 0);
  }

  private closeShowcasePreview(): void {
    if (this.showcasePreviewProductId) {
      situReachIfLoaded()?.Showcase.close();
      this.showcasePreviewProductId = null;
    }
    this.previewProductObj = undefined;
  }

  // --- Deep-link restore + URL sync ---

  // Restore the pill / view / previewed subject from the captured deep-link.
  // Called once the partner list has loaded (companies must exist to select a
  // pill). Runs at most once.
  private applyDeepLink(): void {
    if (this.deepLinkApplied || !this.pendingDeepLink) return;
    this.deepLinkApplied = true;
    const dl = this.pendingDeepLink;
    this.pendingDeepLink = null;
    this.restoring = true;

    // Approvals view (builders only — the toggle no-ops for suppliers).
    if (dl.view === 'approvals' && this.selectedCompany?.type === this.builderType && !this.approvalView) {
      this.toggleApprovalView();
    }

    // Builder/supplier pill — match the loaded partner list (or the company itself).
    if (dl.builder) {
      const company = this.allPartners.find(c => c.id === dl.builder)
        ?? (this.selectedCompany?.id === dl.builder ? (this.selectedCompany as unknown as ICompany) : null);
      if (company) this.onPartnerFilterSelected(company);
    }

    this.restoring = false;

    // Subject preview — only meaningful in the uploads view. Deferred a tick so
    // the pill's product/range reload has kicked off first. Also expand the
    // accordion section the subject lives in, so it's visible in context (not
    // just previewed on the right).
    if (dl.subject && !this.approvalView) {
      const type = this.subjectTypeFromToken(dl.subjectType);
      setTimeout(() => {
        this.openPreview(dl.subject!, type);
        this.expandSectionFor(type);
      }, 0);
    } else {
      this.syncUrl();
    }
  }

  // Open the accordion group a deep-linked subject belongs to (Your Products /
  // Your Ranges) so it's shown in its list, not just previewed. Deferred so the
  // panel's ViewChild has resolved after the uploads view rendered.
  private expandSectionFor(type?: FileVaultSubjectType): void {
    setTimeout(() => {
      if (type === FileVaultSubjectType.Product) this.productsPanel?.open();
      else if (type === FileVaultSubjectType.Range) this.rangesPanel?.open();
    }, 0);
  }

  // Mirror the current pill / view / previewed subject into the query string so
  // the URL is shareable and bookmarkable. `replaceUrl` keeps it out of history.
  private syncUrl(): void {
    if (this.restoring) return;
    const queryParams = {
      builder: this.partner?.id ?? null,
      view: this.approvalView ? 'approvals' : null,
      subject: this.previewSubjectId ?? null,
      subjectType: this.previewSubjectId ? (this.subjectTypeToToken(this.previewSubjectType) ?? null) : null,
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private subjectTypeToToken(t?: FileVaultSubjectType): string | undefined {
    switch (t) {
      case FileVaultSubjectType.Product: return 'product';
      case FileVaultSubjectType.Range: return 'range';
      case FileVaultSubjectType.Company: return 'company';
      default: return undefined;
    }
  }

  private subjectTypeFromToken(tok?: string): FileVaultSubjectType | undefined {
    switch (tok) {
      case 'product': return FileVaultSubjectType.Product;
      case 'range': return FileVaultSubjectType.Range;
      case 'company': return FileVaultSubjectType.Company;
      default: return undefined;
    }
  }

  public canDrop(drag: CdkDrag, drop: CdkDropList): boolean {
    return drag.data?.localType !== FileType.PDF;
  }

  public cantDrop = () => false;

  public toggleApprovalView(): void {
    this.approvalView = !this.approvalView;
    this.filterPartners();
    this.partner = null;
    this.partnerFiles.next({ approved: [], pending: [], rejected: [] });
    this.getViewContent();
    this.syncUrl();
  }

  async onApprovalDropped(event: CdkDragDrop<MarketingFileReference[], MarketingFileReference[], MarketingFileReference>, approved: boolean) {
    let request = new UpdateMarketingFileApprovalRequest();
    request.companyId = this.selectedCompany!.id!;
    request.fileVaultReferenceCompanySubjectRelationId = event.previousContainer.data[event.previousIndex].relationId!;
    request.isApproved = approved;
    this.situViewClient.createOrUpdateMarketingFileApproval(request).then(
      (res) => {
        if (res.success) {
          this.snackBar.open(`${approved ? 'Approved' : 'Rejected'} file`, 'OK', { duration: 2000 });
          transferArrayItem(
            event.previousContainer.data,
            event.container.data,
            event.previousIndex,
            event.currentIndex,
          );
          event.item.data.border = this.getBorderImage(approved ? ['green'] : ['red']);
        } else {
          this.snackBar.open(`Failed to update file approval. Please try again`, 'OK', { duration: 2000 });
        }
      }
    )
  }

  public onMaterialDropped(event: CdkDragDrop<MarketingFileReference[], MarketingFileReference[], MarketingFileReference>, subjectId: string): void {

    if (event.previousContainer === event.container && event.container.data !== this.files[subjectId].scope[this.scope].available) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      if (event.container.data !== this.files[subjectId].scope[this.scope].available) {
        this.files[subjectId].scope[this.scope].edited = true;
      }
    } else if (!event.container.data.map(x => x.id).includes(event.previousContainer.data[event.previousIndex].id)) {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      this.files[subjectId].scope[this.scope].edited = true;
    }
  }

  public onSaveAttachedMaterials(subjectId: string): void {
    let request = new UpdateMarketingMaterialFiltersRequest();
    request.companyId = this.selectedCompany?.id!;
    request.subjectId = subjectId;
    request.filterCompanyId = this.partner?.id || undefined;
    const attached = this.files[subjectId]?.scope[this.scope]?.attached ?? [];
    request.fileVaultRelationIds = attached.map(f => f.relationId!);
    // Resend each file's current role — the backend defaults a filter to Gallery
    // when no role is supplied, so omitting these would silently demote any
    // hero/brochure/warranty back to a gallery slide on an ordinary reorder-save.
    request.roles = {};
    attached.forEach(f => { if (f.relationId) request.roles![f.relationId] = this.fileRole(f); });
    this.situViewClient.updateMarketingMaterialFilters(request).then(
      (res) => {
        if (res.success) {
          this.snackBar.open(`Updated attached files`, 'OK', { duration: 2000 });
          this.files[subjectId].scope[this.scope].edited = false;
        } else {
          this.snackBar.open(`Failed to updated attached files. Please try again`, 'OK', { duration: 2000 });
        }
      }
    )
  }

  async dropHandler(ev: DragEvent, subjectId: string, subjectType: FileVaultSubjectType, zone: HTMLDivElement) {
    // Prevent the file from being opened in the browser
    ev.preventDefault();

    if (!ev.dataTransfer || !ev.dataTransfer.items) {
      return;
    }

    var newfiles: File[] = [];
    var items = ev.dataTransfer.items;
    var uploadableTypes = imageTypes.concat(this.pdfType, this.videoType);
    for (let item of items) {
      if (item.kind === "file" && uploadableTypes.includes(item.type)) {
        var file = item.getAsFile();
        if (file) {
          newfiles.push(file);
        }
      }
    }
    if (newfiles?.length !== items?.length) {
      this.snackBar.open(`${items?.length - newfiles?.length} file(s) failed or are not supported. `, 'OK', { duration: 2000 });
    }

    this.onFileChosen(newfiles, subjectId, subjectType);
    zone.classList.remove('file-hover');
  }

  public dragHandler(event: DragEvent, zone: HTMLDivElement) {
    if (event.dataTransfer?.types?.includes("Files")) {
      zone.classList.add('file-hover');
    }
    event.preventDefault();
  }

  private getBorderImage(colors: string[]): string {
    if (!colors?.length) return '';
    const step = 360 / colors.length;
    const stops = colors
      .map((c, i) => {
        const start = i * step;
        const end = (i + 1) * step;
        return `${c} ${start}deg ${end}deg`;
      })
      .join(', ');

    return `conic-gradient(${stops}) 1`;
  }

  private updateColors(): void {
    if (!this.approvalView) {
      const partners = this.allPartners.filter(p => p.type === CompanyType.Builder);
      if (this.selectedCompany?.type === CompanyType.Builder) {
        partners.unshift(this.selectedCompany!);
      }
      Object.values(this.files).forEach((subject: MarketingSubject) => {
        subject.allFiles.forEach((file: MarketingFileReference) => {
          if (this.partner) {
            // assign single builder approval border
            const approval = file.approvals?.find(a => a.companyId === this.scope)?.isApproved;
            const colour = approval === undefined ? 'orange' : approval ? 'green' : 'red';
            file.border = this.getBorderImage([colour]);
            // tooltip
            if (approval === undefined) {
              file.tooltip = `Pending Approval`;
            } else if (approval) {
              file.tooltip = `Approved`;
            } else {
              file.tooltip = `Rejected`;
            }
          } else {
            //assign border based on all approval statuses
            const colours: string[] = [];
            let approvals: string[] = [];
            let pendings: string[] = []
            let rejections: string[] = [];
            if (file.approvals) {
              partners.forEach(partner => {
                const approval = file.approvals!.find(a => a.companyId === partner.id);
                if (approval) {
                  if (approval.isApproved) {
                    colours.push('green');
                    approvals.push(partner.name!);
                  } else {
                    colours.push('red');
                    rejections.push(partner.name!);
                  }
                } else {
                  colours.push('orange');
                  pendings.push(partner.name!);
                }
              });
            }
            colours.sort((c, o) => (c === 'green' ? -1 : c === 'orange' ? 0 : 1) - (o === 'green' ? -1 : o === 'orange' ? 0 : 1));
            file.border = this.getBorderImage(colours);

            // tooltip
            if (approvals.length === partners.length) {
              file.tooltip = `Fully Approved`;
              return;
            }
            file.tooltip = `
            ${approvals.length > 0 ? approvals.length + ' Approved: ' + approvals.join(', ') : ''}
            ${pendings.length > 0 ? pendings.length + ' Pending: ' + pendings.join(', ') + '' : ''}
            ${rejections.length > 0 ? rejections.length + ' Rejected: ' + rejections.join(', ') : ''}`;
          }
        });
      });
    }
  }

  public onPartnerFilterSelected(partner: ICompany | null): void {
    this.partner = partner;
    this.syncUrl();
    if (!this.approvalView) {
      let scope = partner?.id || 'null';
      Object.values(this.files).forEach((subject: MarketingSubject) => {
        if (!subject.scope[scope]) {
          subject.scope[scope] = {
            attached: [],
            available: subject.allFiles.filter(f => (f.localType !== FileType.MAP && f.localType !== FileType.THUMBNAIL)),
            edited: false
          }
        }
      });
      this.updateColors();

      if (this.scope !== scope) {
        //load products shared to this company
        this.products = Array.from({ length: 1 });
        this.pagesLoading = {};
        this.scope = scope;
        this.filteredRanges = this.allRanges.filter(r => !this.partner?.id || r.rangeAuthorisedCompanies?.some(rac => rac.companyId === this.partner?.id));
      }

      // If a product showcase preview is open, re-mount with the new
      // builder so the user can flip partners and see the re-skin live.
      if (this.showcasePreviewProductId) {
        this.mountShowcasePreview(this.showcasePreviewProductId);
      }
    }
    else {
      this.loadPartnerMaterials();
    }
  }

  public onProductTypeSelected(productTypeName: string): void {
    if (productTypeName) {
      const selection = this.allProductTypes.find(pt => pt.name?.toLowerCase()?.includes(productTypeName?.toLowerCase()));
      this.productFilterGroup.controls.type.setValue(selection);
    }
  }

  public getFileDownloadUrl(file: FileVaultReferenceRelation): string {
    return this.fileVaultService.getFileDownloadUrl(file);
  }

  // --- Tile-content completeness (preview side panel) ---
  // Mirrors the SituReach tile's data sources so a marketer can see, at a
  // glance, which product fields still need filling to enrich the showcase
  // tile — naming the source property and linking straight to the product.
  public hasRender(product: Product2): boolean {
    return !!product.previewArtifactId;
  }

  public hasDescription(product: Product2): boolean {
    return !!product.description?.trim();
  }

  // --- Tile role slots (hero / brochure / warranty) ---
  // A slot is filled straight from the completeness panel: uploading into it
  // uploads the file AND attaches it with the role in one step. State reflects
  // the current partner scope (the Default/partner pill).
  private scopeCompanyId(): string | null {
    return this.scope === 'null' ? null : this.scope;
  }

  // The role a file carries in the current scope (its filter's role; Gallery if untagged).
  public fileRole(f: MarketingFileReference): MarketingRole {
    const cid = this.scopeCompanyId();
    return f.filters?.find(fl => (fl.companyId ?? null) === cid)?.role ?? MarketingRole.Gallery;
  }

  private roleFile(productId: string, role: MarketingRole): MarketingFileReference | undefined {
    return this.files[productId]?.scope[this.scope]?.attached?.find(f => this.fileRole(f) === role);
  }

  // 'missing' → nothing tagged for this slot; 'pending' → tagged but the builder
  // hasn't approved it yet (only meaningful when a partner is selected); 'live' → on the tile.
  public roleState(productId: string, role: MarketingRole): 'missing' | 'pending' | 'live' {
    const f = this.roleFile(productId, role);
    if (!f) return 'missing';
    if (this.partner) {
      return f.approvals?.find(a => a.companyId === this.partner!.id)?.isApproved ? 'live' : 'pending';
    }
    return 'live';
  }

  public roleStateLabel(productId: string, role: MarketingRole): string {
    switch (this.roleState(productId, role)) {
      case 'live': return 'On tile';
      case 'pending': return 'Pending approval';
      default: return '';
    }
  }

  // Upload a file straight into a tile slot: upload it, then attach it with the
  // role in the current scope (preserving the roles of anything already attached).
  public async onUploadForRole(files: FileList | null | undefined, productId: string, role: MarketingRole): Promise<void> {
    const file = files?.[0];
    if (!file || !this.selectedCompany?.id) return;
    this.snackBar.open('Uploading...', 'OK', { duration: 2000 });
    const res = await this.uploadWithThumbnailAndApproval(this.selectedCompany.id, productId, FileVaultSubjectType.Product, file);
    if (!res?.relationId) {
      this.snackBar.open('Upload failed', 'OK', { duration: 2000 });
      return;
    }
    await this.saveAttachedWithRole(productId, res.relationId, role);
    this.loadMaterials([productId]);
  }

  private async saveAttachedWithRole(productId: string, newRelationId: string, newRole: MarketingRole): Promise<void> {
    const attached = this.files[productId]?.scope[this.scope]?.attached ?? [];
    const relationIds = attached.map(f => f.relationId!).filter(id => id && id !== newRelationId);
    const roles: { [id: string]: MarketingRole } = {};
    attached.forEach(f => { if (f.relationId) roles[f.relationId] = this.fileRole(f); });
    relationIds.push(newRelationId);
    roles[newRelationId] = newRole;

    const request = new UpdateMarketingMaterialFiltersRequest();
    request.companyId = this.selectedCompany!.id!;
    request.subjectId = productId;
    request.filterCompanyId = this.partner?.id || undefined;
    request.fileVaultRelationIds = relationIds;
    request.roles = roles;
    const response = await this.situViewClient.updateMarketingMaterialFilters(request);
    if (!response.success) {
      this.snackBar.open('Failed to attach file', 'OK', { duration: 2000 });
    }
  }

  // New product editor route (under the catalogue), where description lives.
  // Opened in a new tab so the marketer keeps their place on this page.
  public getProductEditUrl(productId: string | undefined): string {
    return productId ? `/products/${productId}` : '';
  }

  public async onFileChosen(files: any, subjectId: string, subjectType: FileVaultSubjectType, expansionPanel?: MatExpansionPanel) {
    if (this.selectedCompany?.id && files?.length > 0) {
      this.snackBar.open(`Uploading Files...`, 'OK', { duration: 2000 });
      Promise.all(([...files] as File[]).map(file => this.uploadWithThumbnailAndApproval(this.selectedCompany!.id!, subjectId, subjectType, file))).then(
        (items) => {
          this.snackBar.open(`${items?.length} file(s) uploaded`, 'OK', { duration: 2000 });
          this.loadMaterials([subjectId]);
          expansionPanel?.open();
        }
      );
    }
  }

  public async uploadWithThumbnailAndApproval(companyId: string, subjectId: string, subjectType: FileVaultSubjectType, file: File, path?: string): Promise<FileVaultReferenceRelation | undefined> {
    let res = await this.fileVaultService.uploadBlob(companyId, subjectId, subjectType, FileVaultCategory.MarketingMaterial, file);

    if (res && this.selectedCompany!.type === CompanyType.Builder) {
      // auto approve themselves for builders
      let approvalRequest = new UpdateMarketingFileApprovalRequest();
      approvalRequest.companyId = companyId;
      approvalRequest.fileVaultReferenceCompanySubjectRelationId = res.relationId!;
      approvalRequest.isApproved = true;
      try {
        let approval = await this.situViewClient.createOrUpdateMarketingFileApproval(approvalRequest);
      } catch (e) {
        console.error('Auto approval failed', e);
      }
    }

    if (res && res.fileContentType === this.videoType) {
      const thumb = await this.getVideoThumbnail(file, res.id!);
      if (thumb) {
        await this.fileVaultService.uploadBlob(this.selectedCompany!.id!, subjectId, subjectType, FileVaultCategory.MarketingMaterial, thumb, 'thumbnail');
      }
    }
    return res;
  }

  public checkImgDimensions(thumb: HTMLImageElement, mat: MarketingFileReference) {

    // don't add bad aspect flag in approval view
    if (this.approvalView) {
      delete mat.badAspect;
      return;
    };

    const w = thumb.naturalWidth;
    const h = thumb.naturalHeight;

    if (w / h > 0.55 || w / h < 0.45) {
      mat.badAspect = true;
    }
  }

  public async getVideoThumbnail(file: File, name: string, timeSec = 1): Promise<File> {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "auto";
    video.src = url;

    // wait for video to be loaded
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => {
        if (timeSec > video.duration) timeSec = video.duration - 0.1;
        video.currentTime = timeSec;
      };
      video.onseeked = () => resolve();
      video.onerror = (e) => reject(e);
    });

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);

    const blob: Blob = await new Promise((res) =>
      canvas.toBlob((b) => res(b!), "image/png")
    );

    return new File([blob], name + '.thumbnail.png', { type: 'image/png' });
  }

  public onRemoveDrop(event: CdkDragDrop<MarketingFileReference[], MarketingFileReference[], MarketingFileReference>, subjectId: string, expansionPanel: MatExpansionPanel): void {
    this.onRemove(event.item.data, subjectId, expansionPanel);
  }

  public onRemove(file: MarketingFileReference, subjectId: string, expansionPanel?: MatExpansionPanel): void {

    let map = file.map;
    let thumbnail = file.thumbnail;

    this.confirmDialogService.open({
      title: 'Remove File',
      message: `Are you sure you wish to remove '${file.fileName}' permanently?`,
      cancelText: 'No',
      confirmText: 'Yes',
      actionIcon: 'delete',
      actionColour: 'warn'
    } as ConfirmDialogData);

    this.confirmDialogService.confirmed().subscribe((res => {
      if (res) {
        this.remove(file, expansionPanel);
        if (map) {
          this.remove(map, expansionPanel);
        }
        if (thumbnail) {
          this.remove(thumbnail, expansionPanel);
        }
      }
    }));
  }

  private remove(file: FileVaultReferenceRelation, expansionPanel?: MatExpansionPanel): void {
    this.fileVaultService.removeFileRequest(file.companyId!, file.subjectId!, file).then((
      removed => {
        if (removed.success) {
          // remove item from all local lists
          const sub = this.files[file.subjectId!];
          [sub.allFiles, ...Object.values(sub.scope).map(s => s.attached), ...Object.values(sub.scope).map(s => s.available)].forEach(list => {
            const index = list.map(x => x.id).indexOf(file.id);
            if (index >= 0) list.splice(index, 1);
          });

          if (!this.files[file.subjectId!].allFiles.length) {
            expansionPanel?.close();
          }
        }
      }
    ));
  }

  public onDownload(file: FileVaultReferenceRelation): void {
    this.fileVaultService.download(file);
  }

  protected onSelectedCompanyChange(): void {

    if (this.selectedCompany?.id) {
      this.products = Array.from({ length: 1 });
      this.pagesLoading = {};
      this.partner = null;

      this.approvalView = this.selectedCompany.type === CompanyType.Builder;
      this.getViewContent();

      let request = new ListPartnershipsRequest();
      request.pageNumber = 1;
      request.pageSize = 9999;
      request.searchTerm = '';
      request.companyId = this.selectedCompany!.id!;
      request.partnershipStatuses = [PartnershipStatus.Approved, PartnershipStatus.Reconnected];
      this.identityClient.listPartnerships(request).then(res => {
        if (res.success) {
          let items = [...new Set(res.partnerships?.map(p => p.requesteeCompany).concat(res.partnerships.map(p => p.requesterCompany)))];
          this.allPartners = items.filter(company => !!company && company!.id !== this.selectedCompany!.id) as ICompany[];
          this.filterPartners();
        } else {
          this.allPartners = [];
          this.partners.next([]);
        }
        this.applyDeepLink();
      });
    }
  }

  private getViewContent(): void {
    if (!this.approvalView) {
      Promise.all([this.getProductPage(0, true) || Promise.resolve(undefined), this.loadRanges()])
        .then(([products, ranges]) => {
          const ids = [this.selectedCompany!.id!];
          if (ranges) ids.push(...ranges.filter((r: Range) => r).map((r: Range) => r.id!));
          if (products) ids.push(...products.filter((p: Product2) => p).map((p: Product2) => p.id!));
          this.loadMaterials(ids, true);
        });
    } else {
      this.situViewClient.listPendingFileApprovalCount(this.selectedCompany?.id!).then(
        (res) => {
          if (res.success) {
            this.partnerPendingFiles = res.pendingApprovals || {};
          } else {
            this.snackBar.open("Failed to load pending approvals", undefined, { duration: 3000 });
          }
        }
      );
    }
  }

  private filterPartners(): void {
    if (this.approvalView) {
      this.partners.next(this.allPartners.filter(
        company => company.type === CompanyType.Manufacturer
      ) as ICompany[]);
    } else {
      const next = this.allPartners.filter(
        company => company.type === CompanyType.Builder
      ) as ICompany[];
      if (this.selectedCompany?.type === CompanyType.Builder) {
        // allow builders to see themselves in the partner list for sharing purposes
        next.unshift(this.selectedCompany);
      }

      this.partners.next(next);
    }
  }

  private loadProductTypes() {
    return this.productService.LoadProductTypesWithColor()
      .then((productTypes: ProductTypeWithColor[]) => {
        this.allProductTypes = productTypes;
        return productTypes;
      });
  }

  private loadRanges(): Promise<Range[] | undefined> {
    return this.situViewClient.listRanges(new ListRangesRequest(
      {
        companyId: this.selectedCompany?.id,
        resourceType: ResourceType.Product,
        includeDeleted: false,
        pageNumber: 1,
        pageSize: 9999
      }))
      .then((res: ListRangesResponse) => {
        this.allRanges = res.items || [];
        this.allRanges = this.allRanges.filter(f => f.name !== 'All' && f.name !== 'Unassigned');
        this.allRanges.sort((a, b) => a.name!.localeCompare(b.name!));
        if (this.partner) {
          this.filteredRanges = this.allRanges.filter(r => r.rangeAuthorisedCompanies?.some(rac => rac.companyId === this.partner?.id));
        } else {
          this.filteredRanges = this.allRanges;
        }
        return this.allRanges;
      })
      .catch((r) => {
        this.snackBar.open("Failed to load ranges, please try again", undefined, { duration: 3000 });
        return undefined;
      })
  }

  public getProductPage(product: number, noMaterials?: boolean): Promise<Product2[] | undefined> | undefined {
    const page = Math.floor(product / this.productsPageSize);
    if (this.pagesLoading[page] !== undefined) {
      return;
    }
    return this.loadProducts(page, noMaterials);
  }

  trackProduct(index: number, item: Product2): string | number {
    return item?.id || index;
  }

  private async loadProducts(page: number, noMaterials = false): Promise<Product2[] | undefined> {

    this.pagesLoading[page] = true;
    const request = new NewListProductsRequest();
    request.pageSize = this.productsPageSize;
    request.pageNumber = page + 1;

    request.companyId = this.selectedCompany?.id;
    request.includeBrand = false;
    request.includeCurrentState = false;
    request.includeProductSpecifications = false;
    request.includeRanges = true;
    request.filterEnabled = true;
    request.sharedToId = this.partner?.id;
    request.filterCompanyId = this.selectedCompany?.id;

    const filters = this.productFilterGroup.getRawValue();
    if (filters.type) request.filterProductTypeId = filters.type.id;
    if (filters.name) request.searchTerm = filters.name;


    const result = await this.situViewClient.newListProducts(request);
    if (result.success) {
      const total = result.totalItems || 0;
      if (!this.totalItems || this.totalItems !== total) {
        this.products = Array.from({ length: total });
        this.totalItems = total;
      }

      const products = result.items || [];
      this.products.splice(page * this.productsPageSize, products.length, ...products);
      this.products = [...this.products];
      this.pagesLoading[page] = false;

      if (!noMaterials) {
        this.loadMaterials(products.map(p => p.id!));
      }

      return result.items;
    } else {
      this.snackBar.open("Failed to load products, please try again", undefined, { duration: 3000 });
    }
  }

  private async loadPartnerMaterials(): Promise<void> {
    this.breadcrumbService.setIsLoading(true);
    if (this.partner?.id) {
      let partnerFiles: PartnerFiles = { approved: [], pending: [], rejected: [] };
      const response = await this.situViewClient.listMarketingMaterials(new ListMarketingMaterialsRequest({
        companyId: this.partner.id,
        requestingCompanyId: this.selectedCompany!.id,
      }));
      if (response.success) {
        // removing mapping files from approval view

        const thumnails = response.marketingFiles?.filter(file => file.filePath === '/thumbnail') || [];
        response.marketingFiles = response.marketingFiles?.filter(file => file.filePath !== '/map' && file.filePath !== '/thumbnail');

        response.marketingFiles?.forEach((file: MarketingFileReference) => {
          //localised type for ngifs, etc.

          this.assignType(file);
          if (file.localType === FileType.VIDEO) {
            const thumb = thumnails.find(t => t.fileName!.includes(file.id!));
            if (thumb) {
              file.thumbnail = thumb;
            }
          }

          const approval = file.approvals?.find(a => a.companyId === this.selectedCompany?.id);
          if (approval) {
            if (approval.isApproved) {
              partnerFiles.approved.push(file);
              file.border = this.getBorderImage(['green']);
            } else {
              partnerFiles.rejected.push(file);
              file.border = this.getBorderImage(['red']);
            }
          } else {
            partnerFiles.pending.push(file);
            file.border = this.getBorderImage(['orange']);
          }
        });
      }
      else {
        this.snackBar.open("Failed to load marketing material, please try again", undefined, { duration: 3000 });
      }
      this.partnerFiles.next(partnerFiles);
    }
    this.breadcrumbService.setIsLoading(false);
  }

  private assignType(file: MarketingFileReference): void {
    if (this.imagetypes.includes(file.fileContentType!)) {
      if (file.fileName!.includes('.thumbnail')) {
        file.localType = FileType.THUMBNAIL;
      } else {
        file.localType = FileType.IMAGE;
      }
    } else if (file.fileContentType === this.pdfType) {
      file.localType = FileType.PDF;
      file.bigram = file.fileName?.slice(0, 2);
    } else if (file.fileContentType === this.videoType) {
      file.localType = FileType.VIDEO;
    } else if (file.fileContentType === this.jsonType) {
      file.localType = FileType.MAP;
    }
  }

  private loadMaterials(subjectIds: string[], all?: boolean): void {
    this.breadcrumbService.setIsLoading(true);
    const companyId: string = this.selectedCompany!.id!;
    this.situViewClient.listMarketingMaterials(new ListMarketingMaterialsRequest({
      companyId: companyId,
      subjectIds: subjectIds,
    }))
      .then((res: ListMarketingMaterialsResponse) => {

        let files: Record<string, MarketingSubject> = {};
        if (res.success) {

          // sort files by most number of approvals, then pending, then rejections
          res.marketingFiles?.sort((a, b) => {
            const aApprovals = a.approvals?.filter(appr => appr.isApproved).length || 0;
            const bApprovals = b.approvals?.filter(appr => appr.isApproved).length || 0;
            if (aApprovals !== bApprovals) {
              return bApprovals - aApprovals;
            }
            const aRejections = a.approvals?.filter(appr => appr.isApproved === false).length || 0;
            const bRejections = b.approvals?.filter(appr => appr.isApproved === false).length || 0;
            if (aRejections !== bRejections) {
              return aRejections - bRejections;
            }
            return 0;
          });

          res.marketingFiles?.forEach((fileRef: MarketingFileReference) => {
            this.assignType(fileRef);

            if (!(fileRef.subjectId! in files)) {
              files[fileRef.subjectId!] = { allFiles: [], scope: {} };
            }
            files[fileRef.subjectId!].allFiles.push(fileRef);

            fileRef.positions = {};
            fileRef.filters?.forEach(filter => {
              let cid = filter.companyId || 'null';
              fileRef.positions![cid] = filter.position!;
              if (!(cid in files[fileRef.subjectId!].scope)) {
                files[fileRef.subjectId!].scope[cid] = { attached: [], available: [], edited: false };
              }
              files[fileRef.subjectId!].scope[cid].attached.push(fileRef);
            });
          });

          Object.values(files).forEach((subject) => {
            Object.entries(subject.scope).forEach(([cid, scope]) => {
              scope.available = subject.allFiles.filter(f => (f.localType !== FileType.MAP && f.localType !== FileType.THUMBNAIL) && !scope.attached.includes(f));
              scope.attached.sort((a, o) => a.positions![cid] - o.positions![cid]);
            });

            // attach meta files
            subject.allFiles.filter(meta => (meta.localType === FileType.MAP || meta.localType === FileType.THUMBNAIL)).forEach(meta => {
              const file = subject.allFiles.find(f => f.id === meta.fileName?.slice(0, meta.fileName.indexOf('.')));
              if (file) {
                if (meta.localType === FileType.MAP) {
                  file.map = meta;
                } else if (meta.localType === FileType.THUMBNAIL) {
                  file.thumbnail = meta;
                }
              }
            });
          });

          if (all) {
            this.files = files;
          }
          Object.assign(this.files, files);

          //populate empty datasets
          this.onPartnerFilterSelected(this.partner);
          this.breadcrumbService.setIsLoading(false);
        }
      })
      .catch((e) => {
        this.snackBar.open("Failed to load marketing material, please try again" + e.message, undefined, { duration: 3000 });
        this.breadcrumbService.setIsLoading(false);
        return undefined;
      })
  }

  onOpenEditor(material: MarketingFileReference, subjectId: string, subjectType?: FileVaultSubjectType, isPreviewOnly: boolean = false): void {
    this.dialog.open<FileMapEditorComponent, FileMapEditorDialogData, FileMapEditorDialogResult>(FileMapEditorComponent, {
      data: {
        file: material,
        subjectId: subjectId,
        subjectType: subjectType,
        isPreviewOnly: isPreviewOnly,
        resources: subjectType ?
          this.files[subjectId].allFiles.filter(f => (f.localType !== FileType.MAP && f.localType !== FileType.THUMBNAIL) && f.id !== material.id)
          : [],
      } as FileMapEditorDialogData,
      width: '70vw',
      height: '70vh',
    }).afterClosed()
      .subscribe((result) => {
        if (result && this.selectedCompany?.id) {
          if (result.action === 'save') {
            this.loadMaterials([subjectId]);
          } else if (result.action === 'delete') {
            delete material.map;
          }
        }
      });
  }

  private onLoadInteractions(material: MarketingFileReference): void {
    const map = material.map;
    if (map && map.uri) {
      fetch(map.uri).then(res => {
        if (!res.ok) throw new Error('Failed to fetch map data');
        return res.json();
      }).then(data => {
        const filedata = data as FileMap;
        //timeout to fix angular cdr race conditions

        filedata.interactions.forEach(i => {
          i.target = this.files[material.subjectId!].allFiles.find(f => f.id === i.targetId);
        });

        material.mapData = filedata;
      })
        .catch(err => {
          this.snackBar.open('Failed to load file map:', 'OK', { duration: 2000 });
        });
    }
  }

  public onPreviewBack(): void {
    if (this.showcasePreviewProductId) {
      this.closeShowcasePreview();
      this.dialog.closeAll();
      this.clearPreviewSubject();
      return;
    }
    if (this.previewStack.length > 1) {
      this.previewStack.pop();
      this.marketingFilePreview = this.previewStack[this.previewStack.length - 1];
      this.activePreview = 0;
    } else {
      this.marketingFilePreview = null;
      this.previewStack = [];
      this.activePreview = 0;
      this.dialog.closeAll();
      this.clearPreviewSubject();
    }
  }

  private clearPreviewSubject(): void {
    this.previewSubjectId = null;
    this.previewSubjectType = undefined;
    this.syncUrl();
  }

  public onRowPreview(event: MouseEvent, subjectId: string, subjectType?: FileVaultSubjectType): void {
    event.preventDefault();
    event.stopPropagation();
    this.openPreview(subjectId, subjectType);
  }

  // Open the preview pane for a subject (product showcase, or range/company
  // slideshow). Extracted from onRowPreview so the deep-link restore can drive
  // it without a DOM event. Mirrors the subject into the URL.
  public openPreview(subjectId: string, subjectType?: FileVaultSubjectType): void {
    this.previewSubjectId = subjectId;
    this.previewSubjectType = subjectType;
    this.syncUrl();

    // For products, swap the legacy slideshow for the SituReach showcase —
    // gives the marketer exactly what a builder's audience would see
    // (auto-generated SituReach tile as slide 1, marketing files as 2..N).
    if (subjectType === FileVaultSubjectType.Product) {
      this.marketingFilePreview = null;
      this.previewStack = [];
      this.activePreview = 0;
      this.showcasePreviewProductId = subjectId;
      this.previewProductObj = this.products?.find(p => p?.id === subjectId);
      // On a deep link the product often isn't in the loaded virtual-scroll page
      // yet, so it's not in `this.products` — fetch it directly so the tile-content
      // completeness card still renders. Guard against a race where the user has
      // since previewed a different product.
      if (!this.previewProductObj) {
        this.productService.GetProduct(subjectId, false, false, false, true).then(p => {
          if (p && this.showcasePreviewProductId === subjectId) {
            this.previewProductObj = p as Product2;
          }
        });
      }
      // Likewise the product's marketing materials may not be loaded on a deep
      // link — without them the hero/brochure/warranty slots read as 'missing'.
      if (!this.files[subjectId]) {
        this.loadMaterials([subjectId]);
      }
      this.mountShowcasePreview(subjectId);
      if (window.innerWidth < 1280) {
        this.dialog.open(this.phonePreview, { width: 'fit-content', height: '90vh' });
      }
      return;
    }

    this.closeShowcasePreview();

    this.activePreview = 0;
    this.previewStack = [];
    // gather all attached materials for this subject, if its a product add range  materiasl, then company materials as well
    let materials: MarketingFileReference[] = [];


    const getAttached = (subject: string) =>
      this.files[subject]?.scope[this.scope]?.attached?.length > 0
        ? this.files[subject]?.scope[this.scope]?.attached
        : this.files[subject]?.scope['null']?.attached;

    const subjectMaterials = getAttached(subjectId);
    if (subjectMaterials) materials.push(...subjectMaterials);

    if (subjectType === FileVaultSubjectType.Range) {
      const range = this.allRanges.find(r => r.id === subjectId);
      if (range) {
        const companyFiles = this.files[range.companyId!]?.scope?.[this.scope]?.attached ?? this.files[range.companyId!]?.scope?.['null']?.attached;
        if (companyFiles) materials.push(...companyFiles);
      }
    }

    // parse interaction maps for showcase
    materials.forEach(mat => { if (mat.map) this.onLoadInteractions(mat); });
    this.marketingFilePreview = materials;
    this.previewStack.push(this.marketingFilePreview);

    if (window.innerWidth < 1280) {
      this.dialog.open(this.phonePreview, {
        width: 'fit-content',
        height: '90vh',
      });
    }
  }

  onPreview(material: MarketingFileReference, appendToStack = false): void {
    if (!material) return;
    if (!material.localType) this.assignType(material);
    if (material.map) this.onLoadInteractions(material);

    const isSmallScreen = window.innerWidth < 1280; //threshold for the mobile previwer

    if (material.localType === FileType.PDF) {
      this.onOpenEditor(material, material.subjectId!, undefined, true);
    } else {
      this.activePreview = 0;
      this.marketingFilePreview = [material];
      if (appendToStack) this.previewStack.push(this.marketingFilePreview);
      if (isSmallScreen) {
        if (window.innerWidth < 1280) {
          if (this.dialog.openDialogs.length === 0) {
            this.dialog.open(this.phonePreview, {
              width: 'fit-content',
              height: '90vh',
            });
          }
        }
      }
    }
  }
}